import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ModalComponent} from '../../../core/components/modal/modal.component';
import {ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors, FormGroup} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {NgClass, NgIf, NgFor} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {CreatorDashboardService} from '../../services/creator/creator-dashboard.service';

type InstagramModalStep = 'link' | 'share';

@Component({
  selector: 'app-share-instagram-modal',
  standalone: true,
  imports: [
    ModalComponent,
    ReactiveFormsModule,
    TranslocoPipe,
  ],
  templateUrl: './share-instagram-modal.html',
  styleUrl: './share-instagram-modal.scss'
})
export class ShareInstagramModal {
  @Input() open = false;
  @Input() profileLink = '';
  @Output() close = new EventEmitter<void>();

  public step: InstagramModalStep = 'link';
  public hasCopiedProfileLink = false;
  public isSubmitting = false;
  public form: FormGroup;

  public constructor(
    private readonly fb: FormBuilder,
    private readonly transloco: TranslocoService,
    private readonly toastr: ToastrService,
    private readonly creatorDashboardService: CreatorDashboardService
  ) {
    this.form = this.fb.group({
      instagramUrl: ['', [Validators.required, this.urlValidator, this.instagramProfileValidator]]
    });
  }

  public get instagramControl(): AbstractControl {
    return this.form.get('instagramUrl') as AbstractControl;
  }

  public onClose(): void {
    this.resetFlow();
    this.close.emit();
  }

  public goToNextStep(): void {
    if (!this.form.valid) {
      this.instagramControl?.markAsTouched();
      return;
    }

    this.step = 'share';
    this.hasCopiedProfileLink = false;
  }

  public copyProfileLink(): void {
    if (!this.profileLink) {
      this.toastr.error(
        this.transloco.translate('creator.dashboard.instagram-modal.copy.error'),
        this.transloco.translate('creator.dashboard.instagram-modal.title')
      );
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      this.toastr.error(
        this.transloco.translate('creator.dashboard.instagram-modal.copy.error'),
        this.transloco.translate('creator.dashboard.instagram-modal.title')
      );
      return;
    }

    navigator.clipboard.writeText(this.profileLink)
      .then(() => {
        this.hasCopiedProfileLink = true;
        this.toastr.success(
          this.transloco.translate('creator.dashboard.instagram-modal.copy.success'),
          this.transloco.translate('creator.dashboard.instagram-modal.title')
        );
      })
      .catch(() => {
        this.toastr.error(
          this.transloco.translate('creator.dashboard.instagram-modal.copy.error'),
          this.transloco.translate('creator.dashboard.instagram-modal.title')
        );
      });
  }

  public submit(): void {
    if (!this.hasCopiedProfileLink || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.creatorDashboardService.submitInstagramLink(this.instagramControl.value).subscribe({
      next: () => {
        this.toastr.success(
          this.transloco.translate('creator.dashboard.instagram-modal.submit.success'),
          this.transloco.translate('creator.dashboard.instagram-modal.title')
        );
        this.isSubmitting = false;
        this.onClose();
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('creator.dashboard.instagram-modal.submit.error'),
          this.transloco.translate('creator.dashboard.instagram-modal.title')
        );
        this.isSubmitting = false;
      }
    });
  }

  private resetFlow(): void {
    this.step = 'link';
    this.hasCopiedProfileLink = false;
    this.form.reset();
  }

  private urlValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { required: true };
    }

    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  }

  private instagramProfileValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    try {
      const parsed = new URL(value);
      const hostname = parsed.hostname.toLowerCase();
      if (!hostname.endsWith('instagram.com')) {
        return { notInstagram: true };
      }

      const path = parsed.pathname.replace(/\/+$/, '').replace(/^\/+/, '');
      const hasExtraSegments = path.split('/').length > 1;

      if (!path || hasExtraSegments || !/^[a-zA-Z0-9._]+$/.test(path)) {
        return { invalidInstagramProfile: true };
      }

      return null;
    } catch {
      return { invalidUrl: true };
    }
  }
}
